import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Icons } from './Icons';
import {
  getNewFeaturesSinceLastVisit,
  setLastSeenVersion,
  hasSeenCurrentVersion,
  isReturningUser,
  VERSION_INFO,
  type NewFeature
} from '../config/version';

interface WhatsNewModalProps {
  onNavigate?: (path: string) => void;
  /** Disable showing the modal (e.g., when onboarding is active) */
  disabled?: boolean;
}

export function WhatsNewModal({ onNavigate, disabled = false }: WhatsNewModalProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [features, setFeatures] = useState<NewFeature[]>([]);

  useEffect(() => {
    // Don't show if disabled (e.g., onboarding is active)
    // Also close if already open and becomes disabled
    if (disabled) {
      if (isOpen) {
        setIsOpen(false);
      }
      return;
    }

    // Don't show "What's New" to first-time visitors (they should see onboarding instead)
    if (!isReturningUser()) {
      return;
    }

    // Check if user has seen the current version
    if (!hasSeenCurrentVersion()) {
      const newFeatures = getNewFeaturesSinceLastVisit();
      if (newFeatures.length > 0) {
        setFeatures(newFeatures);
        setIsOpen(true);
      }
    }
  }, [disabled, isOpen]);

  const handleClose = () => {
    setLastSeenVersion();
    setIsOpen(false);
  };

  const handleFeatureClick = (feature: NewFeature) => {
    if (feature.path && onNavigate) {
      onNavigate(feature.path);
      handleClose();
    }
  };

  if (!isOpen || features.length === 0) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.6)',
          backdropFilter: 'blur(4px)',
          zIndex: 9999,
          animation: 'fadeIn 0.2s ease',
        }}
        onClick={handleClose}
      />

      {/* Modal */}
      <div
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          background: 'var(--card)',
          borderRadius: '16px',
          padding: '0',
          maxWidth: '480px',
          width: '90%',
          maxHeight: '80vh',
          overflow: 'hidden',
          zIndex: 10000,
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
          animation: 'slideUp 0.3s ease',
        }}
      >
        {/* Header */}
        <div
          style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            padding: '24px',
            color: 'white',
            position: 'relative',
          }}
        >
          <button
            onClick={handleClose}
            style={{
              position: 'absolute',
              top: '16px',
              right: '16px',
              background: 'rgba(255, 255, 255, 0.2)',
              border: 'none',
              borderRadius: '8px',
              padding: '8px',
              cursor: 'pointer',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icons.X size={20} />
          </button>

          <div style={{ fontSize: '32px', marginBottom: '8px' }}>🎉</div>
          <h2 style={{ fontSize: '24px', fontWeight: 700, marginBottom: '4px' }}>
            {t('whatsNew.title', "What's New")}
          </h2>
          <p style={{ fontSize: '14px', opacity: 0.9 }}>
            {t('whatsNew.subtitle', 'Version')} {VERSION_INFO.version}
          </p>
        </div>

        {/* Features list */}
        <div
          style={{
            padding: '16px',
            maxHeight: '400px',
            overflowY: 'auto',
          }}
        >
          {features.map((feature, index) => (
            <div
              key={feature.id}
              onClick={() => handleFeatureClick(feature)}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '16px',
                padding: '16px',
                borderRadius: '12px',
                marginBottom: index < features.length - 1 ? '8px' : 0,
                background: 'var(--hover)',
                cursor: feature.path ? 'pointer' : 'default',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                animation: `fadeSlideIn 0.3s ease ${index * 0.1}s both`,
              }}
              onMouseEnter={(e) => {
                if (feature.path) {
                  e.currentTarget.style.transform = 'translateX(4px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateX(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <div
                style={{
                  fontSize: '28px',
                  width: '48px',
                  height: '48px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'var(--card)',
                  borderRadius: '12px',
                  flexShrink: 0,
                }}
              >
                {feature.icon || '✨'}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                  <h3 style={{ fontSize: '15px', fontWeight: 600, color: 'var(--text)' }}>
                    {feature.title}
                  </h3>
                  <NewBadge />
                </div>
                <p style={{ fontSize: '13px', color: 'var(--muted)', lineHeight: '1.5' }}>
                  {feature.description}
                </p>
                {feature.path && (
                  <span
                    style={{
                      fontSize: '12px',
                      color: 'var(--accent)',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px',
                      marginTop: '8px',
                    }}
                  >
                    {t('whatsNew.tryIt', 'Try it')} <Icons.ChevronRight size={14} />
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: '16px 24px',
            borderTop: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <span style={{ fontSize: '12px', color: 'var(--muted)' }}>
            {features.length} {t('whatsNew.newFeatures', 'new features')}
          </span>
          <button
            onClick={handleClose}
            style={{
              padding: '10px 20px',
              borderRadius: '8px',
              border: 'none',
              background: 'var(--accent)',
              color: 'white',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'opacity 0.2s ease',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.9')}
            onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
          >
            {t('whatsNew.gotIt', 'Got it!')}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translate(-50%, -45%);
          }
          to {
            opacity: 1;
            transform: translate(-50%, -50%);
          }
        }
        @keyframes fadeSlideIn {
          from {
            opacity: 0;
            transform: translateX(-10px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
      `}</style>
    </>
  );
}

/**
 * Small "New" badge component for use in navigation/menus
 */
export function NewBadge({ size = 'small' }: { size?: 'small' | 'tiny' }) {
  const styles = {
    small: {
      fontSize: '9px',
      padding: '2px 6px',
    },
    tiny: {
      fontSize: '8px',
      padding: '1px 4px',
    },
  };

  return (
    <span
      style={{
        display: 'inline-block',
        background: 'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)',
        color: 'white',
        borderRadius: '4px',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        animation: 'pulse 2s ease-in-out infinite',
        ...styles[size],
      }}
    >
      NEW
    </span>
  );
}

/**
 * Check if a specific navigation item should show "New" badge
 */
export function shouldShowNewBadge(path: string): boolean {
  const features = getNewFeaturesSinceLastVisit();
  return features.some(f => f.path === path);
}
